// React-Query cache keys, centralised so cross-entity invalidations stay
// consistent. Each key is a tuple of [entity, scope, …]; cross-cutting
// queries (e.g. audit-log) prefix the entity they describe.

export const queryKeys = {
  reservoirs: {
    all: ["reservoirs"] as const,
    list: (params?: Record<string, unknown>) =>
      ["reservoirs", "list", params ?? {}] as const,
    detail: (id: string) => ["reservoirs", "detail", id] as const,
  },
  grows: {
    all: ["grows"] as const,
    list: (params?: Record<string, unknown>) =>
      ["grows", "list", params ?? {}] as const,
    detail: (id: string) => ["grows", "detail", id] as const,
  },
  plantProfiles: {
    all: ["plant-profiles"] as const,
    list: () => ["plant-profiles", "list"] as const,
    detail: (id: string) => ["plant-profiles", "detail", id] as const,
  },
  waterSources: {
    all: ["water-sources"] as const,
    list: () => ["water-sources", "list"] as const,
    detail: (id: string) => ["water-sources", "detail", id] as const,
  },
  nutrients: {
    all: ["nutrients"] as const,
    list: () => ["nutrients", "list"] as const,
    detail: (id: string) => ["nutrients", "detail", id] as const,
  },
  recipes: {
    all: ["recipes"] as const,
    list: () => ["recipes", "list"] as const,
    detail: (id: string, version?: number) =>
      version === undefined
        ? (["recipes", "detail", id, "latest"] as const)
        : (["recipes", "detail", id, version] as const),
  },
  calibrations: {
    all: ["nutrient-calibrations"] as const,
    list: (params?: Record<string, unknown>) =>
      ["nutrient-calibrations", "list", params ?? {}] as const,
    detail: (id: string) => ["nutrient-calibrations", "detail", id] as const,
  },
  refillSessions: {
    all: ["refill-sessions"] as const,
    list: (params?: Record<string, unknown>) =>
      ["refill-sessions", "list", params ?? {}] as const,
    detail: (id: string) => ["refill-sessions", "detail", id] as const,
  },
  refillSteps: {
    forSession: (sessionID: string) =>
      ["refill-steps", "session", sessionID] as const,
    detail: (id: string) => ["refill-steps", "detail", id] as const,
  },
  // The persisted AI conversation for a refill session (GET …/ai/turns),
  // reconstructed from llm_calls. Invalidated after every AI turn so the
  // wizard's shared thread + the detail page's AI trail stay fresh.
  aiTurns: {
    forSession: (sessionID: string) =>
      ["ai-turns", "session", sessionID] as const,
  },
  growReadings: {
    forGrow: (growID: string) => ["grow-readings", "grow", growID] as const,
    latest: (growID: string) => ["grow-readings", "latest", growID] as const,
  },
  pendingActions: {
    all: ["pending-actions"] as const,
    forGrow: (growID: string, includeDismissed?: boolean) =>
      ["pending-actions", "grow", growID, includeDismissed ?? false] as const,
    // Prefix of every forGrow(growID, *) key — invalidate this so BOTH the
    // open (`false`) and include-dismissed (`true`) variants refetch. (React
    // Query prefix-matches, and `[…,growID,false]` is not a prefix of
    // `[…,growID,true]`, so the boolean-suffixed key misses the other variant.)
    forGrowAll: (growID: string) =>
      ["pending-actions", "grow", growID] as const,
  },
  auditLog: {
    forEntity: (entityType: string, entityID: string) =>
      ["audit-log", entityType, entityID] as const,
    tenant: ["audit-log", "tenant"] as const,
  },
  llmCalls: {
    all: ["llm-calls"] as const,
    list: (params?: Record<string, unknown>) =>
      ["llm-calls", "list", params ?? {}] as const,
    detail: (id: string) => ["llm-calls", "detail", id] as const,
  },
  me: ["me"] as const,
} as const;
